import React, { useCallback, useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Loader } from 'lucide-react';
import { parseOzmapColor, isPointInPolygon } from '../../utils/geolocationUtils';
import { getMapTileAttribution, getMapTileUrl } from '../../config/mapTiles';
import { getOzmapBoxStyle } from '../../utils/ozmapBoxStyle';

const OzmapDisplay = ({ trade, darkMapEnabled, isVisible, ozmapData, isLoading, error, zoom = 16, className }) => {
    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const layerGroupsRef = useRef(null);
    if (!layerGroupsRef.current) {
        layerGroupsRef.current = {
            regioes: L.layerGroup(),
            cabos: L.layerGroup(),
            postes: L.layerGroup(),
            caixas: L.layerGroup(),
            tradeMarker: L.layerGroup(), // Camada para o marcador principal
        };
    }

    // Armazena renderizadores de canvas dedicados para cada pane
    const renderersRef = useRef({});

    // Guarda a última visualização desejada (centro/zoom) para poder reaplicá-la
    // quando o mapa sai de um contêiner oculto (acordeão no mobile).
    const viewRef = useRef(null);

    // Extrai propriedades primitivas para evitar re-renderizações desnecessárias
    const tradeLat = trade?.coordinates?.lat;
    const tradeLng = trade?.coordinates?.lng;
    const tradeAddress = trade?.address;

    // Recalcula o tamanho do mapa e, opcionalmente, recentraliza.
    // Necessário porque o Leaflet mede o contêiner na inicialização: se ele estiver
    // com display:none (acordeão fechado), o mapa fica 0x0 e não carrega tiles/vetores.
    const refreshMapSize = useCallback((recenter = false) => {
        const map = mapRef.current;
        if (!map) return;

        // pan: true (padrão) preserva o centro geográfico ao mudar de tamanho.
        map.invalidateSize({ animate: false });

        if (recenter && viewRef.current) {
            const { lat, lng, zoom: viewZoom } = viewRef.current;
            map.setView([lat, lng], viewZoom, { animate: false });
        }
    }, []);

    // Observa o contêiner: assim que ele ganha dimensões reais (acordeão aberto,
    // rotação de tela, etc.) o mapa é redimensionado e recentralizado.
    useEffect(() => {
        const container = mapContainerRef.current;
        if (!container || typeof ResizeObserver === 'undefined') return undefined;

        let hadSize = container.clientWidth > 0 && container.clientHeight > 0;

        const observer = new ResizeObserver(() => {
            const hasSize = container.clientWidth > 0 && container.clientHeight > 0;
            if (!hasSize) {
                hadSize = false;
                return;
            }
            const becameVisible = !hadSize;
            hadSize = true;
            refreshMapSize(becameVisible);
        });

        observer.observe(container);
        return () => observer.disconnect();
    }, [refreshMapSize]);

    // Efeito para invalidar o tamanho do mapa quando se torna visível
    useEffect(() => {
        if (!isVisible) return undefined;
        // O ResizeObserver já cobre o caso normal; este timeout cobre navegadores
        // que aplicam a mudança de layout depois do frame atual.
        const timeoutId = setTimeout(() => refreshMapSize(true), 100);
        return () => clearTimeout(timeoutId);
    }, [isVisible, refreshMapSize]);

    // 1. Inicialização do Mapa (Executa apenas uma vez)
    useEffect(() => {
        if (!mapContainerRef.current || mapRef.current) return;

        mapRef.current = L.map(mapContainerRef.current, {
            preferCanvas: true, // Importante para html2canvas funcionar corretamente com vetores
            zoomSnap: 0.1,
            zoomDelta: 0.1,
            layers: [
                layerGroupsRef.current.regioes,
                layerGroupsRef.current.cabos,
                layerGroupsRef.current.postes,
                layerGroupsRef.current.caixas,
                layerGroupsRef.current.tradeMarker,
            ]
        });

        mapRef.current.createPane('regioesPane').style.zIndex = 410;
        mapRef.current.createPane('cabosPane').style.zIndex = 420;
        mapRef.current.createPane('postesPane').style.zIndex = 610;
        mapRef.current.createPane('caixasPane').style.zIndex = 620;
        mapRef.current.createPane('tradeMarkerPane').style.zIndex = 700;

        // Cria renderizadores Canvas explícitos para cada pane personalizada
        renderersRef.current = {
            regioes: L.canvas({ pane: 'regioesPane' }),
            cabos: L.canvas({ pane: 'cabosPane' }),
            postes: L.canvas({ pane: 'postesPane' }),
            caixas: L.canvas({ pane: 'caixasPane' }),
        };

        // Remove camadas de tile iniciais se houver (segurança)
        mapRef.current.eachLayer(layer => {
            if (layer instanceof L.TileLayer) {
                mapRef.current.removeLayer(layer);
            }
        });
    }, []);

    // 1.1 Destrói a instância do Leaflet ao desmontar, evitando vazamento de
    // listeners/handlers de toque (o modal monta e desmonta o mapa a cada abertura).
    useEffect(() => () => {
        if (mapRef.current) {
            mapRef.current.remove();
            mapRef.current = null;
        }
    }, []);

    // 2. Atualização da Camada de Tiles (Depende de darkMapEnabled)
    useEffect(() => {
        if (!mapRef.current) return;

        mapRef.current.eachLayer(layer => {
            if (layer instanceof L.TileLayer) {
                mapRef.current.removeLayer(layer);
            }
        });

        L.tileLayer(
            getMapTileUrl(darkMapEnabled),
            {
                attribution: getMapTileAttribution(darkMapEnabled),
                maxZoom: 22,
                maxNativeZoom: 19,
            }
        ).addTo(mapRef.current);
    }, [darkMapEnabled]);

    // 3. Atualização do Marcador de Troca (Depende de tradeLat, tradeLng, tradeAddress, zoom, ozmapData.regioes)
    // ozmapData.regioes é usado para encontrar o nome do setor no popup
    useEffect(() => {
        if (!mapRef.current || !tradeLat || !tradeLng) return;

        layerGroupsRef.current.tradeMarker.clearLayers();

        viewRef.current = { lat: Number(tradeLat), lng: Number(tradeLng), zoom };
        mapRef.current.setView([tradeLat, tradeLng], zoom);
        // Se o contêiner já estiver visível mas o mapa ainda tiver medidas antigas
        // (ex.: acordeão aberto antes das coordenadas chegarem), força o recálculo.
        refreshMapSize();

        const tradeIcon = L.divIcon({
            html: `
                <svg width="38" height="38" viewBox="0 0 38 38" style="filter: drop-shadow(0 4px 6px rgba(0,0,0,0.4));">
                    <path fill="#f97316" d="M19,0 C12.373,0 7,5.373 7,12 C7,21.875 19,38 19,38 S31,21.875 31,12 C31,5.373 25.627,0 19,0 Z"></path>
                    <path fill="white" d="M19 8 L16 18 L20 18 L17 25 L22 15 L18 15 L21 8 Z"></path>
                </svg>
            `,
            className: 'bg-transparent border-0',
            iconSize: [38, 38],
            iconAnchor: [19, 38],
            popupAnchor: [0, -38]
        });

        let tradeRegionName = 'N/A';
        if (ozmapData?.regioes && Array.isArray(ozmapData.regioes)) {
            const foundRegion = ozmapData.regioes.find(regiao =>
                regiao.coordinates && isPointInPolygon({ lat: tradeLat, lng: tradeLng }, regiao.coordinates)
            );
            if (foundRegion) {
                tradeRegionName = foundRegion.name;
            }
        }

        // 'ready' com 0 é um resultado válido; 'error' significa que a API não
        // respondeu e exibir "0" seria uma informação errada.
        const clientsStatus = ozmapData?.clientsStatus;
        const clientsLabel = clientsStatus === 'ready'
            ? ozmapData.clientsCount.toLocaleString('pt-BR')
            : clientsStatus === 'error'
                ? 'Não disponível'
                : 'Calculando...';

        const popupContent = `
            <b>LOCAL DA TROCA</b><br>
            <b>Endereço:</b> ${tradeAddress || 'N/A'}<br>
            <b>Setor:</b> ${tradeRegionName}<br>
            <b>Clientes (raio 500 m):</b> ${clientsLabel}<br>
            <b>Coords:</b> ${Number(tradeLat).toFixed(6)}, ${Number(tradeLng).toFixed(6)}
        `;

        L.marker([tradeLat, tradeLng], {
            icon: tradeIcon,
            pane: 'tradeMarkerPane'
        }).bindPopup(popupContent)
          .addTo(layerGroupsRef.current.tradeMarker);

        // Adiciona círculo ao grupo tradeMarker para ser limpo corretamente
        L.circle([tradeLat, tradeLng], {
            radius: 500,
            color: '#3b82f6',
            fillOpacity: 0.1,
            weight: 2
        }).addTo(layerGroupsRef.current.tradeMarker);

    }, [tradeLat, tradeLng, tradeAddress, zoom, ozmapData, refreshMapSize]); // ozmapData dependency needed for popup info

    // 4. Atualização das Camadas Ozmap (Depende de ozmapData)
    useEffect(() => {
        if (!mapRef.current || !ozmapData) return;

        layerGroupsRef.current.regioes.clearLayers();
        layerGroupsRef.current.cabos.clearLayers();
        layerGroupsRef.current.postes.clearLayers();
        layerGroupsRef.current.caixas.clearLayers();

        (ozmapData.regioes || []).forEach(regiao => {
            if (Array.isArray(regiao.coordinates) && regiao.coordinates.length > 0) {
                const regionColor = parseOzmapColor(regiao.color);
                L.polygon(regiao.coordinates, {
                    color: regionColor,
                    weight: 2,
                    fillOpacity: 0.2,
                    pane: 'regioesPane',
                    renderer: renderersRef.current.regioes
                })
                .bindPopup(`<b>Região:</b> ${regiao.name || 'N/A'}`)
                .addTo(layerGroupsRef.current.regioes);
            }
        });

        if (ozmapData.cabos) {
            (ozmapData.cabos || []).forEach(cabo => {
                const cableType = cabo.cableType?.code || '';
                if (cableType.toLowerCase().includes('drop padrão')) {
                    return;
                }

                let coords = [];
                const parsePoint = (pt) => {
                    if (Array.isArray(pt) && pt.length >= 2) return [pt[1], pt[0]];
                    if (pt && typeof pt === 'object' && pt.coords) return parsePoint(pt.coords);
                    return null;
                };

                if (cabo.boxA) {
                    const ptA = parsePoint(cabo.boxA.coords);
                    if (ptA) coords.push(ptA);
                }

                if (cabo.poles && Array.isArray(cabo.poles)) {
                    cabo.poles.forEach(poleEntry => {
                        if (poleEntry.id) {
                            const ptPole = parsePoint(poleEntry.id.coords);
                            if (ptPole) coords.push(ptPole);
                        }
                    });
                }

                if (cabo.boxB) {
                    const ptB = parsePoint(cabo.boxB.coords);
                    if (ptB) coords.push(ptB);
                }

                if (coords.length >= 2) {
                    let colorHex = null;
                    if (cabo.color && typeof cabo.color === 'object' && cabo.color.color) {
                        colorHex = cabo.color.color;
                    } else if (typeof cabo.color === 'string') {
                        colorHex = cabo.color;
                    }
                    if (!colorHex && cabo.cableType?.config?.regular?.color) {
                        colorHex = cabo.cableType.config.regular.color;
                    }
                    if (!colorHex) {
                        colorHex = '#000000';
                    }

                    let weight = 3;
                    if (cabo.cableType?.config?.regular?.weight) {
                        weight = cabo.cableType.config.regular.weight;
                    }

                    const cableColor = parseOzmapColor(colorHex) || '#000000';
                    const cableTypeStr = cabo.cableType?.code || 'N/A';

                    L.polyline(coords, {
                        color: cableColor,
                        weight: weight,
                        opacity: 0.8,
                        pane: 'cabosPane',
                        renderer: renderersRef.current.cabos
                    }).bindPopup(`<b>Cabo:</b> ${cabo.name || 'N/A'}<br><b>Tipo:</b> ${cableTypeStr}`)
                      .addTo(layerGroupsRef.current.cabos);
                }
            });
        }

        (ozmapData.postes || []).forEach(pole => {
            if (pole.lat != null && pole.lng != null) {
                L.circleMarker([pole.lat, pole.lng], {
                    radius: 4,
                    fillColor: "#808080",
                    color: "#000",
                    weight: 1,
                    opacity: 1,
                    fillOpacity: 0.8,
                    pane: 'postesPane',
                    renderer: renderersRef.current.postes
                }).bindPopup(`<b>Poste:</b> ${pole.name || 'N/A'}`)
                  .addTo(layerGroupsRef.current.postes);
            }
        });

        (ozmapData.caixas || []).forEach(caixa => {
            if (caixa.lat != null && caixa.lng != null) {
                L.circleMarker([caixa.lat, caixa.lng], {
                    ...getOzmapBoxStyle(caixa),
                    pane: 'caixasPane',
                    renderer: renderersRef.current.caixas
                }).bindPopup(`<b>Caixa:</b> ${caixa.name || 'N/A'}`)
                  .addTo(layerGroupsRef.current.caixas);
            }
        });

        // Invalida tamanho após adicionar camadas (segurança)
        const timeoutId = setTimeout(() => refreshMapSize(), 100);
        return () => clearTimeout(timeoutId);

    }, [ozmapData, refreshMapSize]);


    return (
        <div className={`relative w-full rounded-lg ${className || 'h-[60vh]'}`}>
            {(!tradeLat || !tradeLng) && (
                <div className="absolute inset-0 z-20 flex items-center justify-center bg-slate-100 dark:bg-slate-700/50 rounded-lg">
                    <p className="text-slate-500">Coordenadas não disponíveis para exibir o mapa.</p>
                </div>
            )}

            {isLoading && <div className="absolute inset-0 bg-black/50 text-white flex items-center justify-center z-10 rounded-lg"><Loader className="animate-spin mr-2"/> Carregando dados do Ozmap...</div>}
            {error && <div className="absolute inset-0 bg-red-500/80 text-white flex items-center justify-center z-10 p-4 text-center rounded-lg">Erro ao carregar dados do Ozmap: {error.message}</div>}
            <div ref={mapContainerRef} className="w-full h-full rounded-lg z-0"></div>
        </div>
    );
};

export default OzmapDisplay;
